import { useRefreshAlgoliaData } from "entities/algolia/hooks/useRefreshAlgoliaData";
import { invalidateModuleListByTagIdQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { invalidateTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { deleteThemeTag } from "entities/themeTags/api/deleteThemeTag";
import { ThemeTag } from "entities/themeTags/types";
import { useSearchThemeTagsData } from "modules/tagsAndMetadataTags/pages/TagsAndMetadataTags/Tabs/Content/ThemeTags/hooks/useSearchThemeTagsData";
import { useThemeTagTypeFilter } from "modules/tagsAndMetadataTags/pages/TagsAndMetadataTags/Tabs/Content/ThemeTags/hooks/useThemeTagTypeFilter";
import React, { FC, useState, PropsWithChildren } from "react";
import { useClearRefinements } from "react-instantsearch";
import { useDrawerState } from "shared/hooks/useDrawerState";
import useToggle from "shared/hooks/useToggle";
import { createContext } from "shared/utils/context";
import { ThemeTagsPageType } from "./types";

const INITIAL_DELAY_MS = 2000;
const REFRESH_INTERVAL_MS = 5000;

const [Provider, useThemeTagsPage] = createContext<ThemeTagsPageType>({
  name: "ThemeTagsPageContext",
  hookName: "useThemeTagsPage",
  providerName: "ThemeTagsPageProvider",
});

const ThemeTagsPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const themeTagsDetailsDrawer = useDrawerState<ThemeTag>();
  const { refreshAlgoliaData } = useRefreshAlgoliaData();
  const [openModal, setOpenModal] = useToggle(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [selectedTag, setSelectedTag] = useState<ThemeTag | null>(null);

  const { refine } = useClearRefinements();

  const {
    filteredValues: selectedMultipleTagTypes,
    filterData: tagTypeFilterData,
    setFilteredValues: setTagTypes,
    isDisabledButton: isDisabledTagTypesButton,
  } = useThemeTagTypeFilter();

  const { searchData, isLoading, tableData } = useSearchThemeTagsData({
    tagTypesIds: selectedMultipleTagTypes,
  });

  const onClearFilters = () => {
    setTagTypes([]);
    refine();
  };

  const onDeleteTag = async () => {
    try {
      if (selectedTag) {
        setLoadingDelete(true);

        await deleteThemeTag(selectedTag.id);
        // Invalidate query and refetch Algolia
        refreshAlgoliaData(INITIAL_DELAY_MS);
        refreshAlgoliaData(REFRESH_INTERVAL_MS);

        invalidateModuleListByTagIdQuery(selectedTag.id);
        themeTagsDetailsDrawer.setDrawerDetails(undefined);
        setSelectedTag(null);
        setOpenModal();
        invalidateTagsListQuery();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDelete(false);
    }
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    themeTagsDetailsDrawer,
    loading: isLoading,
    loadingDelete,
    onClearFilters,
    tagTypeFilterData,
    searchData,
    openModal,
    setOpenModal,
    isDisabledTagTypesButton,
    selectedTag,
    setSelectedTag,
    onDeleteTag,
    tableData,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { ThemeTagsPageProvider, useThemeTagsPage };
